import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  ShoppingBag,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type { RecentActivity } from '../types'

interface RecentActivityFeedProps {
  activities: RecentActivity[]
  isLoading?: boolean
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp

  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`

  const date = new Date(timestamp)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function getActivityIcon(type: RecentActivity['type']) {
  switch (type) {
    case 'order_received':
      return <ShoppingBag className="h-4 w-4" />
    case 'order_completed':
      return <CheckCircle className="h-4 w-4" />
    case 'order_cancelled':
      return <XCircle className="h-4 w-4" />
    case 'status_change':
      return <Clock className="h-4 w-4" />
  }
}

function getActivityColor(type: RecentActivity['type']) {
  switch (type) {
    case 'order_received':
      return 'bg-blue-100 text-blue-700'
    case 'order_completed':
      return 'bg-emerald-100 text-emerald-700'
    case 'order_cancelled':
      return 'bg-red-100 text-red-700'
    case 'status_change':
      return 'bg-amber-100 text-amber-700'
  }
}

function ActivityItem({ activity }: { activity: RecentActivity }) {
  const colorClass = getActivityColor(activity.type)

  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0">
      <div
        className={cn(
          'h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0',
          colorClass
        )}
      >
        {getActivityIcon(activity.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{activity.description}</p>
        <p className="text-xs text-muted-foreground">
          {formatRelativeTime(activity.timestamp)}
        </p>
      </div>
      {activity.amount !== undefined && (
        <span className="text-sm font-medium text-muted-foreground">
          {formatCurrency(activity.amount)}
        </span>
      )}
    </div>
  )
}

function ActivitySkeleton() {
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0">
      <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        <div className="h-3 w-16 bg-muted animate-pulse rounded" />
      </div>
      <div className="h-4 w-16 bg-muted animate-pulse rounded" />
    </div>
  )
}

export function RecentActivityFeed({ activities, isLoading }: RecentActivityFeedProps) {
  const { t } = useTranslation('dashboard')

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold">
          {t('activity.title')}
        </CardTitle>
        <Button asChild variant="ghost" size="sm">
          <Link to="/orders">
            {t('activity.viewAll')}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-0">
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
          </div>
        ) : activities.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('activity.empty')}</p>
          </div>
        ) : (
          <div className="space-y-0">
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
